<template>
	<div>
		<div v-show="!submitted">
			<form
				@submit.prevent="submitRequest()"
				class="space-y-6">
				<div>
					<tw-input
						v-model="name"
						id="name"
						name="name"
						:required="true"
						type="text"
						autocomplete="name"
						label="Name (required)"
						validation="Please enter your name." />
				</div>

				<div>
					<tw-input
						v-model="company"
						id="company"
						name="company"
						:required="true"
						type="text"
						autocomplete="organization"
						label="Company (required)"
						validation="Please enter your company." />
				</div>

				<div>
					<tw-input
						v-model="email"
						id="email"
						name="email"
						:required="true"
						type="email"
						autocomplete="email"
						label="Email address (required)"
						validation="Please enter your email address." />
				</div>

				<div>
					<tw-input
						v-model="phone"
						id="phone"
						name="phone"
						:required="false"
						type="tel"
						autocomplete="tel"
						label="Phone" />
				</div>

				<!-- honeypot: hidden from sighted users, left for bots to fill in -->
				<div
					class="absolute -left-[9999px]"
					aria-hidden="true">
					<label
						for="website"
						class="sr-only">Leave this field blank</label>
					<input
						v-model="honeypot"
						type="text"
						id="website"
						name="website"
						tabindex="-1"
						autocomplete="off">
				</div>

				<cloudflare-turnstile ref="turnstileRef" />

				<div>
					<div class="text-red-600 w-full justify-center text-sm font-bold py-2 h-12 items-end flex">
						{{ message }}
					</div>
					<div>
						<tw-button
							type="submit"
							class="btn-db-yellow w-full"
							:disabled="loading"
							:loading="loading">
							Submit Request
						</tw-button>
					</div>
				</div>
			</form>
		</div>

		<div
			v-show="submitted"
			class="text-sm mt-2 mb-[76px]">
			<p>Thanks, your request has been received. Our team will follow up with you shortly.</p>
		</div>

		<p class="mt-2 leading-6 text-gray-500 dark:text-gray-400 flex justify-center">
			<router-link
				:to="{name: 'SignIn'}"
				class="link">
				Return to Customer Login
			</router-link>
		</p>
	</div>
</template>

<script lang="ts">
	export default { name: 'RequestAccountForm' } // name the component
</script>

<script setup lang="ts">
	// api
	import requestAccount from '@/utilities/requestAccountApi'

	// vue
	import {
		ref,
		computed
	} from 'vue'

	const name = ref("")
	const company = ref("")
	const email = ref("")
	const rawPhone = ref("")
	const honeypot = ref("")
	const loading = ref(false)
	const message = ref<string>('')
	const submitted = ref(false)

	const turnstileRef = ref<{ execute: () => Promise<string> } | null>(null)

	// strips non-digits and formats the phone field as xxx-xxx-xxxx while the user types
	function formatPhone(value: string): string {
		const digits = value.replace(/\D/g, '').slice(0, 10)

		if (digits.length <= 3) {
			return digits
		}
		else if (digits.length <= 6) {
			return `${digits.slice(0, 3)}-${digits.slice(3)}`
		}
		else {
			return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
		}
	}

	// formats on every keystroke so non-digits never render, even briefly;
	// the DOM value is forced directly because tw-input skips its own re-render
	// when the formatted result matches the previous value (e.g. a stripped letter)
	const phone = computed({
		get: () => rawPhone.value,
		set: (value: string) => {
			const formatted = formatPhone(value)
			rawPhone.value = formatted

			const input = document.getElementById('phone') as HTMLInputElement | null
			if (input) {
				input.value = formatted
			}
		}
	})

	// submits the request account form; a filled honeypot field is silently treated as success
	async function submitRequest() {
		try {
			loading.value = true
			message.value = ""

			// check for empty field values
			if (name.value == "") {
				document.getElementById('name')?.focus()
				throw 'Empty name'
			}
			else if (company.value == "") {
				document.getElementById('company')?.focus()
				throw 'Empty company'
			}
			else if (email.value == "") {
				document.getElementById('email')?.focus()
				throw 'Empty email'
			}

			if (honeypot.value) {
				submitted.value = true
				return
			}

			const token = await turnstileRef.value?.execute() ?? ""

			const response = await requestAccount({
				name: name.value,
				company: company.value,
				email: email.value,
				phone: phone.value,
				honeypot: honeypot.value,
				token,
			})

			if (response.result === 'OK') {
				submitted.value = true
			}
			else {
				message.value = "There was an issue submitting your request. Please try again or contact us for assistance."
			}
		}
		catch (error: any) {
			message.value = "There was an issue submitting your request. Please try again or contact us for assistance."
		}
		finally {
			loading.value = false
		}
	}
</script>
